import { useState } from 'react';
import { tx } from '../i18n';
import { pedir } from '../lib/api';
import { permisos } from '../lib/appliance';
import { useConsulta } from '../lib/consulta';
import { cuando, hace } from '../lib/formato';
import { useSesion } from '../sesion/sesion';
import type { ClaveApi, RolDeClave } from '../tipos';
import { Modal } from '../ui/Modal';
import { Cabecera, Consulta, Pastilla, primero, Secreto, Tabla, useAviso, useRefrescar, Vacio, Ventana } from '../ui/piezas';

const ROLES: RolDeClave[] = ['soporte', 'admin'];

/** Alta de una clave: nombre, rol y, si se quiere, hasta cuándo vale. */
function FormClave({ alCerrar, alListo }: { alCerrar: () => void; alListo: (r: { clave: string; registro: ClaveApi }) => void }) {
  const t = tx.clavesApi;
  const [nombre, setNombre] = useState('');
  const [rol, setRol] = useState<RolDeClave>('soporte');
  const [vence, setVence] = useState('');
  return (
    <Ventana
      titulo={t.nueva}
      boton={t.crear}
      alCerrar={alCerrar}
      alEnviar={async () => {
        const falta = primero(!nombre.trim() && t.faltaNombre, !!vence && !(Date.parse(vence) > Date.now()) && t.venceFutura);
        if (falta) return falta;
        const r = await pedir<{ clave: string; registro: ClaveApi }>('/claves-api', {
          metodo: 'POST',
          cuerpo: { nombre: nombre.trim(), rol, vence: vence ? new Date(vence).toISOString() : null },
        });
        alListo(r);
      }}
    >
      <label className="campo" htmlFor="ca-nombre">
        {tx.comun.nombre}
        <input id="ca-nombre" className="entrada" value={nombre} onChange={(e) => setNombre(e.target.value)} placeholder={t.nombreEjemplo} />
        <small className="nota">{t.nombreQue}</small>
      </label>
      <label className="campo" htmlFor="ca-rol">
        {t.rol}
        <select id="ca-rol" className="entrada" value={rol} onChange={(e) => setRol(e.target.value as RolDeClave)}>
          {ROLES.map((r) => (
            <option key={r} value={r}>
              {tx.comun.roles[r]}
            </option>
          ))}
        </select>
        <small className="nota">{t.queRol[rol]}</small>
      </label>
      <label className="campo" htmlFor="ca-vence">
        {t.vence}
        <input id="ca-vence" className="entrada" type="date" value={vence} onChange={(e) => setVence(e.target.value)} />
        <small className="nota">{t.venceQue}</small>
      </label>
    </Ventana>
  );
}

/** La clave, UNA vez. No se cierra con un clic afuera: si se pierde, hay que crear otra. */
function ClaveUnaVez({ registro, clave, alCerrar }: { registro: ClaveApi; clave: string; alCerrar: () => void }) {
  const t = tx.clavesApi;
  return (
    <Modal
      titulo={t.claveDe(registro.nombre)}
      alCerrar={alCerrar}
      cerrarAfuera={false}
      pie={
        <button type="button" className="btn prim" onClick={alCerrar}>
          {t.claveGuardada}
        </button>
      }
    >
      <p className="alerta" role="alert">
        <b>{t.unaSolaVez}</b> {t.claveQue}
      </p>
      <Secreto valor={clave} etiqueta={t.clave} />
      <p className="nota">{t.comoSeUsa}</p>
      <pre className="codigo" style={{ whiteSpace: 'pre-wrap', overflowWrap: 'anywhere', margin: 0 }}>
        {`Authorization: Bearer ${clave.slice(0, 13)}…\nX-KNOC-Appliance: <id>`}
      </pre>
    </Modal>
  );
}

type Accion = { que: 'apagar' | 'borrar'; c: ClaveApi };

/** Claves de API: con qué entra un sistema de un tercero. Solo un admin las maneja. */
export function ClavesApi() {
  const { usuario: yo } = useSesion();
  const puede = permisos(yo?.rol);
  const [aviso, avisar] = useAviso();
  const refrescar = useRefrescar();
  const [nueva, setNueva] = useState(false);
  const [accion, setAccion] = useState<Accion | null>(null);
  const [creada, setCreada] = useState<{ clave: string; registro: ClaveApi } | null>(null);
  const t = tx.clavesApi;
  /** "Ahora" se fija al abrir la pantalla: para decir si una clave ya venció. */
  const [ahora] = useState(() => Date.now());

  const lista = useConsulta<ClaveApi[]>('/claves-api', { enabled: puede.manejarClaves });
  const filas = lista.data ?? [];
  const recargar = () => void refrescar('/claves-api', '/bitacora');
  const cambiar = async (c: ClaveApi, cuerpo: object, texto: string, metodo: 'PATCH' | 'DELETE' = 'PATCH') => {
    try {
      await pedir(`/claves-api/${c.id}`, { metodo, cuerpo: metodo === 'PATCH' ? cuerpo : undefined });
      avisar(texto);
    } catch (e) {
      avisar((e as Error).message, true);
    }
    recargar();
  };

  if (!puede.manejarClaves)
    return (
      <div className="pagina">
        <Cabecera titulo={t.titulo}>{t.que}</Cabecera>
        <Vacio>{t.soloAdmin}</Vacio>
      </div>
    );

  const vencida = (c: ClaveApi) => !!c.vence && Date.parse(c.vence) <= ahora;
  return (
    <div className="pestana-cuerpo">
      <div className="pestana-cab">
        <p className="nota">{t.que}</p>
        <button type="button" className="btn prim" onClick={() => setNueva(true)}>
          {t.nueva}
        </button>
      </div>
      {aviso}

      <section className="panel">
        <Consulta q={lista} vacio={lista.data && filas.length === 0 ? <Vacio>{t.ninguna}</Vacio> : undefined}>
          <div className="scroll-x">
            <Tabla>
              <thead>
                <tr>
                  <th>{tx.comun.nombre}</th>
                  <th>{t.prefijo}</th>
                  <th>{t.rol}</th>
                  <th>{t.estado}</th>
                  <th>{t.vence}</th>
                  <th>{t.ultimoUso}</th>
                  <th>{t.creada}</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {filas.map((c) => (
                  <tr key={c.id} className={c.activa && !vencida(c) ? '' : 'apagado'}>
                    <td>
                      <b>{c.nombre}</b>
                    </td>
                    <td className="d num">{c.prefijo}…</td>
                    <td>{tx.comun.roles[c.rol]}</td>
                    <td>
                      {!c.activa ? (
                        <Pastilla tono="desconocido">{t.apagada}</Pastilla>
                      ) : vencida(c) ? (
                        <Pastilla tono="parcial">{t.vencida}</Pastilla>
                      ) : (
                        <Pastilla tono="arriba">{t.activa}</Pastilla>
                      )}
                    </td>
                    <td className="d">{c.vence ? (cuando(c.vence) ?? '—') : t.noVence}</td>
                    <td className="d">{c.ultimo_uso ? hace(c.ultimo_uso) : t.nunca}</td>
                    <td className="d">
                      {hace(c.creado)} · {c.creado_por}
                    </td>
                    <td className="acciones der">
                      {c.activa ? (
                        <button type="button" className="btn chico" onClick={() => setAccion({ que: 'apagar', c })}>
                          {t.apagar}
                        </button>
                      ) : (
                        <button type="button" className="btn chico" onClick={() => void cambiar(c, { activa: true }, t.encendida(c.nombre))}>
                          {t.encender}
                        </button>
                      )}
                      <button type="button" className="btn chico peligro" onClick={() => setAccion({ que: 'borrar', c })}>
                        {t.borrar}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Tabla>
          </div>
        </Consulta>
      </section>

      {nueva && (
        <FormClave
          alCerrar={() => setNueva(false)}
          alListo={(r) => {
            setNueva(false);
            recargar();
            setCreada(r);
            avisar(t.creadaOk(r.registro.nombre));
          }}
        />
      )}
      {accion?.que === 'apagar' && (
        <Ventana
          titulo={t.apagarA(accion.c.nombre)}
          boton={t.apagar}
          peligro
          alCerrar={() => setAccion(null)}
          alEnviar={async () => {
            await pedir(`/claves-api/${accion.c.id}`, { metodo: 'PATCH', cuerpo: { activa: false } });
            setAccion(null);
            recargar();
            avisar(t.apagadaOk(accion.c.nombre));
          }}
        >
          <p>{t.apagarQue}</p>
        </Ventana>
      )}
      {accion?.que === 'borrar' && (
        <Ventana
          titulo={t.borrarA(accion.c.nombre)}
          boton={t.borrar}
          peligro
          alCerrar={() => setAccion(null)}
          alEnviar={async () => {
            await pedir(`/claves-api/${accion.c.id}`, { metodo: 'DELETE' });
            setAccion(null);
            recargar();
            avisar(t.borradaOk(accion.c.nombre));
          }}
        >
          <p>{t.borrarQue}</p>
        </Ventana>
      )}
      {creada && <ClaveUnaVez registro={creada.registro} clave={creada.clave} alCerrar={() => setCreada(null)} />}
    </div>
  );
}
